/* Casca da Kojeva: cabeçalho fixo "esconde ao descer", modal de busca, gaveta de menu,
   acordeão do rodapé, seletor de país e abertura da gaveta do carrinho ao adicionar.
   O resto da gaveta do carrinho (itens, +/−, remover, subtotal) continua em theme.js. */
(() => {
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
  const routesRoot = window.Shopify?.routes?.root ?? "/";

  /* Foco preso ------------------------------------------------------------- */

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // Elementos fixos não têm offsetParent: medir retângulos é o jeito confiável de saber se está visível.
  const isVisible = (element) => element.getClientRects().length > 0;

  const openTrap = () => $$("[data-kjv-trap]").filter(isVisible).pop() || null;

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Tab") return;
    const container = openTrap();
    if (!container) return;
    const items = $$(FOCUSABLE, container).filter(isVisible);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (!container.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // As gavetas de menu e de carrinho são abertas por theme.js, que só tira o atributo hidden:
  // observar esse atributo é o que permite levar e devolver o foco.
  function watchDrawer(rootSelector, firstFocusSelector) {
    const root = $(rootSelector);
    if (!root || root.dataset.kjvWatch) return;
    root.dataset.kjvWatch = "1";
    let returnTo = null;
    new MutationObserver(() => {
      if (root.hidden) {
        if (returnTo && typeof returnTo.focus === "function") returnTo.focus();
        returnTo = null;
        return;
      }
      returnTo = document.activeElement;
      const target = $(firstFocusSelector, root);
      if (target) requestAnimationFrame(() => target.focus());
    }).observe(root, { attributes: true, attributeFilter: ["hidden"] });
  }

  /* Cabeçalho fixo "on scroll up" ------------------------------------------- */

  function initStickyHeader() {
    const rail = $("[data-kjv-header-rail]");
    if (!rail || rail.dataset.kjvReady) return;
    rail.dataset.kjvReady = "1";
    const header = $("[data-kjv-header]", rail);
    if (!header) return;

    let railTop = 0;
    let headerHeight = 0;
    let lastY = Math.max(0, window.scrollY);
    let fixed = false;
    let queued = false;

    const publishBottom = () => {
      const bottom = Math.max(0, header.getBoundingClientRect().bottom);
      document.documentElement.style.setProperty("--kjv-header-bottom", `${bottom}px`);
    };

    const fix = () => {
      rail.style.height = `${headerHeight}px`;
      rail.classList.add("is-fixed");
      fixed = true;
    };

    const unfix = () => {
      rail.style.height = "";
      rail.classList.remove("is-fixed", "is-hidden", "is-animated");
      fixed = false;
    };

    const measure = () => {
      const wasFixed = fixed;
      if (wasFixed) unfix();
      railTop = rail.getBoundingClientRect().top + window.scrollY;
      // getBoundingClientRect evita o arredondamento de offsetHeight, que daria um pulo de 1 px.
      headerHeight = header.getBoundingClientRect().height;
      if (wasFixed) {
        fix();
        rail.classList.add("is-animated");
      }
      publishBottom();
    };

    const update = () => {
      queued = false;
      const y = Math.max(0, window.scrollY);
      if (y > railTop + headerHeight) {
        if (!fixed) {
          fix();
          rail.classList.add("is-hidden");
          requestAnimationFrame(() => rail.classList.add("is-animated"));
        } else if (y < lastY - 2) {
          rail.classList.remove("is-hidden");
        } else if (y > lastY + 2) {
          rail.classList.add("is-hidden");
        }
      } else if (fixed) {
        unfix();
      }
      lastY = y;
      publishBottom();
    };

    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(update);
    };

    measure();
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", measure);
    if (document.fonts?.ready) document.fonts.ready.then(measure);
  }

  /* Modal de busca ---------------------------------------------------------- */

  function initSearchModal() {
    const modal = $("[data-kjv-search]");
    const toggle = $("[data-kjv-search-open]");
    if (!modal || !toggle || modal.dataset.kjvReady) return;
    modal.dataset.kjvReady = "1";
    const form = $("form", modal);
    const input = $(".kjv-search__input", modal);
    const reset = $("[data-kjv-search-reset]", modal);

    const syncReset = () => {
      if (reset) reset.hidden = input.value.trim() === "";
    };

    const open = () => {
      modal.hidden = false;
      toggle.setAttribute("aria-expanded", "true");
      syncReset();
      requestAnimationFrame(() => input.focus());
    };

    const close = () => {
      if (modal.hidden) return;
      modal.hidden = true;
      toggle.setAttribute("aria-expanded", "false");
      toggle.focus();
    };

    toggle.addEventListener("click", () => (modal.hidden ? open() : close()));
    $$("[data-kjv-search-close]", modal).forEach((button) => button.addEventListener("click", close));
    input.addEventListener("input", syncReset);
    form?.addEventListener("reset", () => {
      requestAnimationFrame(() => {
        syncReset();
        input.focus();
      });
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !modal.hidden) {
        event.stopPropagation();
        close();
      }
    });
    syncReset();
  }

  /* Gaveta de menu: fecha sozinha quando a tela vira desktop ------------------ */

  function initMenuGuard() {
    const wide = window.matchMedia("(min-width: 990px)");
    wide.addEventListener("change", () => {
      const menu = $("[data-dd-menu]");
      if (wide.matches && menu && !menu.hidden) menu.dispatchEvent(new CustomEvent("dd:close"));
    });
  }

  /* Acordeão do rodapé (abaixo de 750 px) ------------------------------------ */

  function initFooterAccordion(root = document) {
    const blocks = $$("[data-kjv-accordion]", root);
    if (!blocks.length) return;
    const narrow = window.matchMedia("(max-width: 749px)");

    const apply = () => {
      blocks.forEach((block) => {
        const toggle = $("[data-kjv-accordion-toggle]", block);
        if (!toggle) return;
        block.classList.toggle("is-collapsed", narrow.matches);
        toggle.setAttribute("aria-expanded", narrow.matches ? "false" : "true");
      });
    };

    blocks.forEach((block) => {
      if (block.dataset.kjvReady) return;
      block.dataset.kjvReady = "1";
      const toggle = $("[data-kjv-accordion-toggle]", block);
      if (!toggle) return;
      toggle.addEventListener("click", () => {
        if (!narrow.matches) return;
        const collapsed = block.classList.toggle("is-collapsed");
        toggle.setAttribute("aria-expanded", collapsed ? "false" : "true");
      });
    });

    apply();
    narrow.addEventListener("change", apply);
  }

  /* Seletor de país: botão de fechar e filtro da lista ----------------------- */

  function initCountryExtras(root = document) {
    $$("[data-dd-country]", root).forEach((wrapper) => {
      if (wrapper.dataset.kjvReady) return;
      wrapper.dataset.kjvReady = "1";
      const list = $("[data-dd-country-list]", wrapper);
      const toggle = $("[data-dd-country-toggle]", wrapper);
      const backdrop = $("[data-dd-country-close]", wrapper);
      if (!list || !toggle) return;

      $$("[data-kjv-country-close]", wrapper).forEach((button) =>
        button.addEventListener("click", () => {
          list.hidden = true;
          if (backdrop) backdrop.hidden = true;
          toggle.setAttribute("aria-expanded", "false");
          toggle.focus();
        }),
      );

      const filter = $("[data-dd-country-filter]", wrapper);
      if (!filter) return;
      const options = $$(".ddc-countryOption", wrapper);
      const empty = $("[data-dd-country-empty]", wrapper);
      filter.addEventListener("input", () => {
        const term = filter.value.trim().toLowerCase();
        let shown = 0;
        options.forEach((option) => {
          const hit = !term || (option.dataset.countryName || "").includes(term);
          option.hidden = !hit;
          if (hit) shown += 1;
        });
        if (empty) empty.hidden = shown > 0;
      });
    });
  }

  /* Abrir a gaveta do carrinho ao adicionar ---------------------------------- */

  const openCartDrawer = () => {
    const trigger = $("[data-dd-cart-open]");
    if (trigger) trigger.click();
    else window.location.href = `${routesRoot}cart`;
  };

  function showAddError(form, message) {
    let note = $("[data-kjv-add-error]", form);
    if (!note) {
      note = document.createElement("p");
      note.dataset.kjvAddError = "";
      note.className = "ddc-addError";
      note.setAttribute("role", "alert");
      form.append(note);
    }
    note.textContent = message || "This product could not be added to your cart.";
  }

  document.addEventListener("submit", async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    // Os formulários com data-dd-add já são tratados por theme.js.
    if (!form || form.matches("[data-dd-add]") || form.hasAttribute("data-kjv-no-drawer")) return;
    event.preventDefault();
    const button = $('[type="submit"]', form);
    button?.setAttribute("aria-busy", "true");
    try {
      const response = await fetch(`${routesRoot}cart/add.js`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: new FormData(form),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        showAddError(form, error.description);
        return;
      }
      $("[data-kjv-add-error]", form)?.remove();
      openCartDrawer();
    } catch (error) {
      showAddError(form, null);
    } finally {
      button?.removeAttribute("aria-busy");
    }
  });

  /* Boot --------------------------------------------------------------------- */

  function init(root = document) {
    initStickyHeader();
    initSearchModal();
    initFooterAccordion(root);
    initCountryExtras(root);
    watchDrawer("[data-dd-menu]", ".kjv-menuDrawer__item");
    watchDrawer("[data-dd-cart]", ".kjv-cart__close");
  }

  initMenuGuard();
  init();
  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
